import { module as BaseModule } from "modujs";

const AUTOPLAY_TIMEOUT = 2000;
const FADE_IN_DURATION = 0.8;
const FADE_IN_DELAY_MS = 150;
const VIMEO_MIN_PLAYBACK_SECONDS = 0.1;

export default class extends BaseModule {
  init() {
    this.wrapper = this.el.closest(".project-video-wrapper");
    this.poster = this.el.dataset.posterUrl;
    this.hasStarted = false;
    this.autoplayBlocked = false;
    this.autoplayTimeout = null;
    this.revealTimeout = null;
    this.vimeoWatchTimeout = null;
    this.gestureButton = null;
    this.cursorTimeout = null;
    this.unsubscribeStore = null;
    this.wasFullscreen = false;
    this.playbackStartedAt = performance.now();
    this.media = null;
    this.skin = this.el.querySelector("video-neutral-skin");

    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.markStarted = this.markStarted.bind(this);
    this.handleGesturePlay = this.handleGesturePlay.bind(this);
    this.handleAutoplayBlocked = this.handleAutoplayBlocked.bind(this);
    this.onPlaybackConfirmed = this.onPlaybackConfirmed.bind(this);
    this.onPlayerReady = this.onPlayerReady.bind(this);
    this.handlePlaybackProgress = this.handlePlaybackProgress.bind(this);
    this.handleVimeoPlaying = this.handleVimeoPlaying.bind(this);
    this.handleStoreChange = this.handleStoreChange.bind(this);

    const start = () => {
      if (!this.el.isConnected) return;
      this.mountMedia();
      this.onPlayerReady();
    };

    if (customElements.get("video-player") && customElements.get("video-neutral-skin")) {
      start();
      return;
    }

    Promise.all([
      customElements.whenDefined("video-player"),
      customElements.whenDefined("video-neutral-skin"),
    ]).then(start);
  }

  mountMedia() {
    if (!this.skin || this.media) return;

    const provider = this.el.dataset.provider;
    const sourceUrl = this.el.dataset.sourceUrl;
    if (!provider || !sourceUrl) return;

    const tagName = provider === "youtube" ? "youtube-video" : "vimeo-video";
    const media = document.createElement(tagName);
    this.media = media;
    // Attach first so the custom element upgrades, then size + source.
    this.skin.appendChild(media);
    this.fillMediaHost(media);

    media.autoplay = true;
    media.muted = true;
    media.playsInline = true;

    if (provider === "youtube") {
      media.source = {
        src: sourceUrl,
        engine: {
          youtube: {
            // Props should drive mute; engine mute ensures the embed URL is muted for autoplay.
            mute: 1,
            rel: 0,
            iv_load_policy: 3,
            disablekb: 1,
            fs: 0,
            enablejsapi: 1,
            origin: window.location.origin,
          },
        },
      };
    } else {
      media.source = {
        src: sourceUrl,
        engine: {
          vimeo: {
            muted: true,
            byline: false,
            portrait: false,
            title: false,
            speed: true,
            badge: false,
          },
        },
      };
    }

    this.bindMediaEvents();
  }

  /** Video.js embed hosts default to 300×150; force fill of the sized skin. */
  fillMediaHost(media) {
    if (!media) return;
    media.style.setProperty("display", "block", "important");
    media.style.setProperty("width", "100%", "important");
    media.style.setProperty("height", "100%", "important");
    media.style.setProperty("min-width", "0", "important");
    media.style.setProperty("min-height", "0", "important");
  }

  bindMediaEvents() {
    if (!this.media) return;

    this.media.addEventListener("timeupdate", this.handlePlaybackProgress);
    this.media.addEventListener("progress", this.handlePlaybackProgress);

    if (this.el.dataset.provider === "vimeo") {
      this.media.addEventListener("playing", this.handleVimeoPlaying);
    }
  }

  onPlayerReady() {
    if (!this.el.store) return;

    this.unsubscribeStore = this.el.store.subscribe(this.handleStoreChange);
    this.scheduleAutoplayFallback();

    if (this.el.dataset.provider === "vimeo") {
      this.watchVimeoPlayback();
    }
  }

  handleStoreChange() {
    const isFullscreen = Boolean(this.el.store?.isFullscreen);

    if (isFullscreen !== this.wasFullscreen) {
      this.wasFullscreen = isFullscreen;

      if (isFullscreen) {
        this.dispatchCustomEvent("player:enterfullscreen");
        document.documentElement.classList.add("player-fullscreen");
        this.startCursorTimer();
        document.addEventListener("mousemove", this.handleMouseMove);
      } else {
        this.dispatchCustomEvent("player:exitfullscreen");
        document.documentElement.classList.remove("player-fullscreen");
        this.clearCursorTimer();
        document.removeEventListener("mousemove", this.handleMouseMove);
        document.documentElement.classList.remove("has-inactive-cursor");
      }
    }

    if (!this.hasStarted && this.hasPlaybackStarted()) {
      this.onPlaybackConfirmed();
    }
  }

  stopVimeoPlaybackWatch() {
    window.clearTimeout(this.vimeoWatchTimeout);
    this.vimeoWatchTimeout = null;
  }

  confirmVimeoPlayback() {
    const embed = this.media?.engine;

    if (!embed?.getCurrentTime || !embed?.getPaused) {
      return Promise.resolve(false);
    }

    return Promise.all([embed.getCurrentTime(), embed.getPaused()])
      .then(([seconds, paused]) => !paused && seconds >= VIMEO_MIN_PLAYBACK_SECONDS)
      .catch(() => false);
  }

  watchVimeoPlayback() {
    const poll = () => {
      if (this.hasStarted) return;

      this.confirmVimeoPlayback().then((isPlaying) => {
        if (this.hasStarted) return;

        if (isPlaying) {
          this.onPlaybackConfirmed();
          return;
        }

        this.vimeoWatchTimeout = window.setTimeout(poll, 150);
      });
    };

    poll();
  }

  scheduleAutoplayFallback() {
    window.clearTimeout(this.autoplayTimeout);
    this.autoplayTimeout = window.setTimeout(() => {
      if (!this.hasStarted && !this.hasPlaybackStarted()) {
        this.handleAutoplayBlocked();
      }
    }, AUTOPLAY_TIMEOUT);
  }

  hasPlaybackStarted() {
    const currentTime =
      this.el.store?.currentTime ?? this.media?.currentTime ?? 0;

    if (this.el.dataset.provider === "vimeo") {
      return currentTime >= VIMEO_MIN_PLAYBACK_SECONDS;
    }

    return currentTime > 0;
  }

  handleVimeoPlaying() {
    if (this.hasStarted) return;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (this.hasStarted) return;

        this.confirmVimeoPlayback().then((isPlaying) => {
          if (this.hasStarted || !isPlaying) return;

          this.onPlaybackConfirmed();
        });
      });
    });
  }

  handlePlaybackProgress() {
    if (this.hasStarted || !this.hasPlaybackStarted()) return;

    this.onPlaybackConfirmed();
  }

  applyPoster(url) {
    this.el.style.setProperty("--player-poster-url", `url("${url}")`);
    this.el.classList.add("has-custom-poster");
  }

  hidePoster() {
    this.el.classList.add("has-started-playing");
    this.el.classList.remove("has-custom-poster");
    this.el.style.removeProperty("--player-poster-url");
  }

  handleAutoplayBlocked() {
    if (this.autoplayBlocked || this.hasStarted || this.hasPlaybackStarted()) return;

    this.autoplayBlocked = true;
    this.el.classList.add("needs-user-gesture");
    this.revealPlayer();

    if (!this.gestureButton) {
      this.gestureButton = document.createElement("button");
      this.gestureButton.type = "button";
      this.gestureButton.className = "player-gesture-overlay";
      this.gestureButton.setAttribute("aria-label", "Play video");
      this.gestureButton.addEventListener("click", this.handleGesturePlay);
      this.el.appendChild(this.gestureButton);
    }
  }

  handleGesturePlay(event) {
    event.preventDefault();

    const playPromise = this.el.store?.play?.() ?? this.media?.play?.();

    if (playPromise !== undefined) {
      playPromise.catch(() => {});
    }
  }

  onPlaybackConfirmed() {
    if (this.hasStarted) return;

    this.markStarted();
    this.revealPlayer();
  }

  markStarted() {
    if (this.hasStarted) return;

    this.hasStarted = true;
    window.clearTimeout(this.autoplayTimeout);
    this.stopVimeoPlaybackWatch();
    this.hidePoster();
    this.el.classList.remove("needs-user-gesture");
    this.removeGestureOverlay();
  }

  getRevealTarget() {
    return this.skin ?? this.el;
  }

  clearRevealState() {
    this.el.removeAttribute("data-player-revealed");
    this.getRevealTarget()?.removeAttribute("data-player-revealed");
  }

  revealPlayer() {
    const timeToPlayMs = Math.round(performance.now() - this.playbackStartedAt);
    console.log(
      `[player] first frame in ${timeToPlayMs}ms (${this.el.dataset.provider}, fading in over ${FADE_IN_DURATION}s after ${FADE_IN_DELAY_MS}ms delay)`,
    );

    window.clearTimeout(this.revealTimeout);
    this.wrapper?.classList.remove("is-video-loading");
    this.el.removeAttribute("data-warming-up");
    this.el.style.removeProperty("visibility");
    this.revealTimeout = window.setTimeout(() => {
      this.getRevealTarget()?.setAttribute("data-player-revealed", "");
      this.el.setAttribute("data-player-revealed", "");
      this.revealTimeout = null;
    }, FADE_IN_DELAY_MS);
  }

  removeGestureOverlay() {
    if (!this.gestureButton) return;

    this.gestureButton.removeEventListener("click", this.handleGesturePlay);
    this.gestureButton.remove();
    this.gestureButton = null;
  }

  startCursorTimer() {
    this.clearCursorTimer();
    this.cursorTimeout = setTimeout(() => {
      document.documentElement.classList.add("has-inactive-cursor");
    }, 4000);
  }

  clearCursorTimer() {
    if (this.cursorTimeout) {
      clearTimeout(this.cursorTimeout);
      this.cursorTimeout = null;
    }
  }

  handleMouseMove() {
    document.documentElement.classList.remove("has-inactive-cursor");
    this.startCursorTimer();
  }

  dispatchCustomEvent(eventName) {
    const event = new CustomEvent(eventName, {
      detail: { player: this.el },
    });
    document.dispatchEvent(event);
  }

  destroy() {
    window.clearTimeout(this.autoplayTimeout);
    window.clearTimeout(this.revealTimeout);
    this.stopVimeoPlaybackWatch();
    this.clearCursorTimer();
    document.removeEventListener("mousemove", this.handleMouseMove);
    this.removeGestureOverlay();
    this.clearRevealState();

    if (typeof this.unsubscribeStore === "function") {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }

    if (this.media) {
      this.media.removeEventListener("timeupdate", this.handlePlaybackProgress);
      this.media.removeEventListener("progress", this.handlePlaybackProgress);
      this.media.removeEventListener("playing", this.handleVimeoPlaying);
      this.media.remove();
      this.media = null;
    }

    document.documentElement.classList.remove("player-fullscreen");
    document.documentElement.classList.remove("has-inactive-cursor");
  }
}
